// src/App.tsx - COMPLETE FIXED VERSION with pitch detail route for Senior Analysts

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DataProvider } from "@/contexts/DataContext";
import { ForgeProvider } from "@/contexts/ForgeContext";
import { SquadAuthProvider, useSquadAuth } from "@/contexts/SquadAuthContext";
import { CompanyAuthProvider, useCompanyAuth } from "@/contexts/CompanyAuthContext";
import AppLayout from "@/components/layout/AppLayout";
import ForgeLayout from "@/components/layout/ForgeLayout";
import CompanyLayout from "@/components/layout/CompanyLayout";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { useToast } from "@/components/ui/use-toast";

// LOGIN PAGES
import FounderLoginPage from "@/pages/FounderLogin";
import AnalystLoginPage from "@/pages/AnalystLogin";
import RegisterPage from "@/pages/Register";

// DASHBOARDS
import FounderDashboard from "@/pages/FounderDashboard";
import ProblemBankAnalystDashboard from "@/pages/ProblemBankAnalystDashboard";
import BusinessAnalystDashboard from "@/pages/BusinessAnalystDashboard";
import SeniorAnalystDashboard from "@/pages/SeniorAnalystDashboard";
import AdminDashboard from "@/pages/AdminDashboard";

// PROFILE PAGES
import FounderProfile from "@/pages/FounderProfile";
import ProfileSettings from "@/pages/ProfileSettings";

// FOUNDER PAGES
import PitchesPage from "@/pages/PitchesPage";
import PitchCreatePage from "@/pages/PitchCreatePage";
import PitchCreateBasics from "@/pages/pitch-create/PitchCreateBasics";
import PitchCreateTraction from "@/pages/pitch-create/PitchCreateTraction";
import PitchCreateCompetition from "@/pages/pitch-create/PitchCreateCompetition";
import PitchCreateFinancial from "@/pages/pitch-create/PitchCreateFinancial";
import PitchCreateGTM from "@/pages/pitch-create/PitchCreateGTM";
import PitchCreateIP from "@/pages/pitch-create/PitchCreateIP";
import PitchCreateRisks from "@/pages/pitch-create/PitchCreateRisks";
import PitchDetailPage from "@/pages/PitchDetailPage";
import NotificationsPage from "@/pages/NotificationsPage";
import EmployeeProfile from "@/pages/EmployeeProfile";
import EmployeeEditProfile from "@/pages/EmployeeEditProfile";
import EmployeeEditAccess from "@/pages/EmployeeEditAccess";
import NotFound from "./pages/NotFound";

// ANALYST PAGES
import SquadInterests from "@/pages/analyst/SquadInterests";
import SeniorAnalystPitches from "@/pages/SeniorAnalystPitches";

// Problem Bank Imports
import ProblemBank from "@/pages/problem-bank/ProblemBank";
import ProblemDetail from "@/pages/problem-bank/ProblemDetail";
import ProblemProgressTracker from "@/pages/problem-bank/ProblemProgressTracker";
import AnalystSquadAssignment from "@/pages/problem-bank/AnalystSquadAssignment";
import ProblemNew from "@/pages/problem-bank/ProblemNew";
import ExternalSubmissions from "@/pages/problem-bank/ExternalSubmissions";
import ReviewSubmission from "@/pages/problem-bank/ReviewSubmission";
import MyAssignments from "@/pages/problem-bank/MyAssignments";
import SeniorAssignmentDashboard from "@/pages/problem-bank/SeniorAssignmentDashboard";
import AnalystPerformanceProfile from "@/pages/problem-bank/AnalystPerformanceProfile";

// Senior Analyst Problem Bank Admin
import AdminProblemBank from "@/pages/admin/ProblemBank";
import AdminProblemDetail from "@/pages/admin/ProblemDetail";
import AdminSubmissions from "@/pages/admin/Submissions";
import AdminProblemNew from "@/pages/admin/ProblemNew";

// Forge Imports
import ForgeLandingPage from "@/pages/forge/ForgeLandingPage";
import SquadAuth from "@/pages/forge/SquadAuth";
import SquadRegistration from "@/pages/forge/SquadRegistration";
import ApplicationSubmitted from "@/pages/forge/ApplicationSubmitted";
import SquadDashboard from "@/pages/forge/SquadDashboard";
import ExternalSubmission from "@/pages/forge/ExternalSubmission";
import SquadMemberProfile from "@/pages/forge/SquadMemberProfile";
import SquadMembersPage from "@/pages/forge/SquadMembersPage";
import SquadProgressTracker from "@/pages/forge/SquadProgressTracker";
import ForgeProblemBank from "@/pages/forge/ProblemBank";
import ForgeProblemDetail from "@/pages/forge/ProblemDetail";

// Company Portal Imports
import CompanyLogin from "@/pages/company/Login";
import CompanyRegister from "@/pages/company/Register";
import CompanyDashboard from "@/pages/company/Dashboard";
import CompanySubmit from "@/pages/company/SubmitProblem";
import CompanySubmissions from "@/pages/company/Submissions";
import CompanySubmissionDetail from "@/pages/company/SubmissionDetail";
import CompanyProfile from "@/pages/company/Profile";
import CompanySettings from "@/pages/company/Settings";
import CompanySupport from "@/pages/company/Support";

// Public Problem Submission
import SubmitProblem from "@/pages/public/SubmitProblem";
import SubmissionThankYou from "@/pages/public/SubmissionThankYou";

// LEGAL PAGES
import PrivacyPage from "@/pages/legal/PrivacyPage";
import TermsPage from "@/pages/legal/TermsPage";
import CookiesPage from "@/pages/legal/CookiesPage";
import IPNoticePage from "@/pages/legal/IPNoticePage";
import SitemapPage from "@/pages/legal/SitemapPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 2000,
      gcTime: 1000 * 60 * 10,
      retry: 3,
    },
  },
});

// ========== ROUTE PROTECTION ==========

function SystemAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isInitialized } = useAuth();
  const { toast } = useToast();
  
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'admin') {
    toast({ title: "Access Denied", description: "Admin access required", variant: "destructive" });
    return <Navigate to="/analyst-login" replace />;
  }
  
  return <>{children}</>;
}

function SeniorAnalystRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isInitialized } = useAuth();
  const { toast } = useToast();
  
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/analyst-login" replace />;
  
  if (user.role !== 'senior_analyst' && user.role !== 'admin') {
    toast({ title: "Access Denied", description: "Senior Analyst access required", variant: "destructive" });
    return <Navigate to="/analyst-dashboard" replace />;
  }
  
  return <>{children}</>;
}

function AnalystRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isInitialized } = useAuth();
  
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/analyst-login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin-portal" replace />;
  if (user.role !== 'analyst' && user.role !== 'senior_analyst') return <Navigate to="/founder-login" replace />;
  
  return <>{children}</>;
}

function FounderRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isInitialized } = useAuth();
  
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/founder-login" replace />;
  if (user.role !== 'founder') return <Navigate to="/analyst-login" replace />;
  
  return <>{children}</>;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isInitialized } = useAuth();
  
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin-portal" replace />;
    if (user.role === 'senior_analyst') return <Navigate to="/senior-analyst" replace />;
    if (user.role === 'analyst') return <Navigate to="/analyst-dashboard" replace />;
    if (user.role === 'founder') return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

function ForgeRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useSquadAuth();
  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  if (!isAuthenticated) return <Navigate to="/forge/auth" replace />;
  return <>{children}</>;
}

function CompanyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useCompanyAuth();
  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  if (!isAuthenticated) return <Navigate to="/company/login" replace />;
  return <>{children}</>;
}

function RegularAnalystDashboardRouter() {
  const { user } = useAuth();
  if (user?.analyst_type === 'problem_bank') return <ProblemBankAnalystDashboard />;
  return <BusinessAnalystDashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* PUBLIC ROUTES */}
      <Route path="/" element={<AuthRoute><FounderLoginPage /></AuthRoute>} />
      <Route path="/founder-login" element={<AuthRoute><FounderLoginPage /></AuthRoute>} />
      <Route path="/analyst-login" element={<AnalystLoginPage />} />
      <Route path="/register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
      <Route path="/submit-problem" element={<SubmitProblem />} />
      <Route path="/submission-thank-you" element={<SubmissionThankYou />} />
      
      {/* LEGAL */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/ip-notice" element={<IPNoticePage />} />
      <Route path="/sitemap" element={<SitemapPage />} />
      
      {/* COMPANY PORTAL */}
      <Route path="/company/login" element={<CompanyLogin />} />
      <Route path="/company/register" element={<CompanyRegister />} />
      <Route element={<CompanyRoute><CompanyLayout /></CompanyRoute>}>
        <Route path="/company/dashboard" element={<CompanyDashboard />} />
        <Route path="/company/submit" element={<CompanySubmit />} />
        <Route path="/company/submissions" element={<CompanySubmissions />} />
        <Route path="/company/submissions/:id" element={<CompanySubmissionDetail />} />
        <Route path="/company/profile" element={<CompanyProfile />} />
        <Route path="/company/settings" element={<CompanySettings />} />
        <Route path="/company/support" element={<CompanySupport />} />
      </Route>
      
      {/* FORGE PORTAL */}
      <Route path="/forge" element={<ForgeLandingPage />} />
      <Route path="/forge/auth" element={<SquadAuth />} />
      <Route path="/forge/register" element={<SquadRegistration />} />
      <Route path="/forge/register/:inviteCode" element={<SquadRegistration />} />
      <Route path="/forge/application-submitted" element={<ApplicationSubmitted />} />
      <Route path="/forge/submit" element={<ExternalSubmission />} />
      <Route element={<ForgeRoute><ForgeProvider><ForgeLayout /></ForgeProvider></ForgeRoute>}>
        <Route path="/forge/dashboard" element={<SquadDashboard />} />
        <Route path="/forge/problem-bank" element={<ForgeProblemBank />} />
        <Route path="/forge/problem/:id" element={<ForgeProblemDetail />} />
        <Route path="/forge/squad" element={<SquadMembersPage />} />
        <Route path="/forge/squad/:memberId" element={<SquadMemberProfile />} />
        <Route path="/forge/progress" element={<SquadProgressTracker />} />
      </Route>
      
      {/* SYSTEM ADMIN PORTAL - User & System Management */}
      <Route element={<SystemAdminRoute><AppLayout /></SystemAdminRoute>}>
        <Route path="/admin-portal" element={<AdminDashboard />} />
        <Route path="/admin-portal/users" element={<AdminDashboard />} />
        <Route path="/admin-portal/system" element={<AdminDashboard />} />
        <Route path="/admin-portal/audit" element={<AdminDashboard />} />
      </Route>
      
      {/* SENIOR ANALYST PORTAL - Problem Bank Management */}
      <Route element={<SeniorAnalystRoute><AppLayout /></SeniorAnalystRoute>}>
        <Route path="/senior-analyst" element={<SeniorAnalystDashboard />} />
        <Route path="/senior-analyst/pitches" element={<SeniorAnalystPitches />} />
        {/* Pitch detail route for Senior Analysts - ADDED */}
        <Route path="/pitch/:id" element={<PitchDetailPage />} />
        <Route path="/senior-analyst/problem-bank" element={<AdminProblemBank />} />
        <Route path="/senior-analyst/problem/:id" element={<AdminProblemDetail />} />
        <Route path="/senior-analyst/problem/new" element={<AdminProblemNew />} />
        <Route path="/senior-analyst/submissions" element={<AdminSubmissions />} />
        <Route path="/senior-analyst/assignments" element={<SeniorAssignmentDashboard />} />
      </Route>
      
      {/* REGULAR ANALYST PORTAL */}
      <Route element={<AnalystRoute><AppLayout /></AnalystRoute>}>
        <Route path="/analyst-dashboard" element={<RegularAnalystDashboardRouter />} />
        <Route path="/analyst/pitches" element={<PitchesPage />} />
        <Route path="/analyst/pitch/:id" element={<PitchDetailPage />} />
        <Route path="/analyst/squad-interests" element={<SquadInterests />} />
        <Route path="/analyst/:id/performance" element={<AnalystPerformanceProfile />} />
        <Route path="/problem-bank" element={<ProblemBank />} />
        <Route path="/problem-bank/problem/:id" element={<ProblemDetail />} />
        <Route path="/problem-bank/problem/:id/progress" element={<ProblemProgressTracker />} />
        <Route path="/problem-bank/problem/:id/squads" element={<AnalystSquadAssignment />} />
        <Route path="/problem-bank/new" element={<ProblemNew />} />
        <Route path="/problem-bank/submissions" element={<ExternalSubmissions />} />
        <Route path="/problem-bank/submissions/review/:id" element={<ReviewSubmission />} />
        <Route path="/problem-bank/my-assignments" element={<MyAssignments />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/profile" element={<ProfileSettings />} />
        <Route path="/employee/:id" element={<EmployeeProfile />} />
        <Route path="/employee/:id/edit" element={<EmployeeEditProfile />} />
        <Route path="/employee/:id/access" element={<EmployeeEditAccess />} />
      </Route>
      
      {/* FOUNDER PORTAL */}
      <Route element={<FounderRoute><AppLayout /></FounderRoute>}>
        <Route path="/dashboard" element={<FounderDashboard />} />
        <Route path="/pitches" element={<PitchesPage />} />
        <Route path="/pitch/new" element={<PitchCreatePage />} />
        <Route path="/pitch/new/basics" element={<PitchCreateBasics />} />
        <Route path="/pitch/new/traction" element={<PitchCreateTraction />} />
        <Route path="/pitch/new/competition" element={<PitchCreateCompetition />} />
        <Route path="/pitch/new/financial" element={<PitchCreateFinancial />} />
        <Route path="/pitch/new/gtm" element={<PitchCreateGTM />} />
        <Route path="/pitch/new/ip" element={<PitchCreateIP />} />
        <Route path="/pitch/new/risks" element={<PitchCreateRisks />} />
        <Route path="/pitch/:id" element={<PitchDetailPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/founder-profile" element={<FounderProfile />} />
        <Route path="/employee/:id" element={<EmployeeProfile />} />
        <Route path="/employee/:id/edit" element={<EmployeeEditProfile />} />
        <Route path="/employee/:id/access" element={<EmployeeEditAccess />} />
      </Route>
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AppContent() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <DataProvider>
            <SquadAuthProvider>
              <CompanyAuthProvider>
                <BrowserRouter basename="/app">
                  <AppRoutes />
                  <PWAInstallPrompt />
                </BrowserRouter>
              </CompanyAuthProvider>
            </SquadAuthProvider>
          </DataProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

const App = () => <AppContent />;
export default App;